import { useFeatureFlagQueryByNameWithAnonymousID } from 'components/layouts/hooks';
import { Box } from 'components/primitives/box';
import { FlexAlignCenterJustifyStart, FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import { Text } from 'components/primitives/typography';
import {
  duplicateCaptainsAtom,
  duplicateCheckboxAtomArrayAtom,
  duplicateSliderAtom,
} from 'components/search/v2/Filter/filterAtomUtils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ReactElement, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useBreakpoint } from 'components/BreakpointProvider';
import { renderAtomCheckButton } from '../fields/AtomCheckButton';
import AtomSlider from '../fields/AtomSlider';
import {
  activitiesAtom,
  activitiesManipulatorAtom,
  activitiesTempAtom,
  activitiesTempManipulatorAtom,
  allFiltersActiveCountAtom,
  boatAmenitiesAtom,
  boatAmenitiesManipulatorAtom,
  boatAmenitiesTempAtom,
  boatCategoriesAtom,
  boatCategoriesManipulatorAtom,
  boatCategoriesTempAtom,
  boatLengthAtom,
  boatLengthTempAtom,
  boatMakesManipulatorAtom,
  boatTypesManipulatorAtom,
  captainOptionsTempAtom,
  durationHoursAtom,
  durationHoursManipulatorAtom,
  durationHoursTempAtom,
  experienceSubTagOptionsAtom,
  instantBookAtom,
  instantBookTempAtom,
  priceAtom,
  priceTempAtom,
  selectedExperienceTagAtom,
  selectedExperienceTagTempAtom,
} from '../filterStore';
import BoatAmenitiesContent from './BoatAmenitiesContent';
import CaptainFilterContent from './CaptainFilterContent';
import CollapsibleFilter from './CollapsibleFilter';
import { DurationFilterContentWithTempAtom } from './DurationFilterContent';
import { ExperienceSubTagsContent } from './ExperienceSubTags';
import InstantBookFilterSwitch, { InstantBookFilterLabel } from './InstantBookFilterSwitch';
import PassengersFilterContent from './PassengersFilterContent';
import SelectedValuesCounter from './SelectedValuesCounter';

interface FilterItem {
  label?: string | ReactElement;
  key?: string;
  hideLabel?: boolean;
  content?: ReactElement;
  openByDefault?: boolean;
  isTop?: boolean;
  isCollapsible?: boolean;
  headerExtraContent?: ReactElement;
  isPassengers?: boolean;
  isCategories?: boolean;
  isPrice?: boolean;
}

const getOtherFilters = (priceIsTop = true): FilterItem[] => [
  {
    label: 'Price (hourly)',
    hideLabel: true,
    content: <AtomSlider atom={priceTempAtom} />,
    openByDefault: true,
    isTop: priceIsTop,
  },
  {
    isCollapsible: false,
    content: <PassengersFilterContent />,
    openByDefault: true,
    isPassengers: true,
    key: 'passengers',
  },
  {
    label: 'Duration',
    isCollapsible: false,
    content: <DurationFilterContentWithTempAtom />,
    openByDefault: true,
  },
  {
    isCollapsible: false,
    label: 'Captain options',
    content: <CaptainFilterContent isDropdown />,
    openByDefault: true,
  },
  {
    label: 'Activities',
    content: <CheckButtonsContent atom={activitiesTempAtom} data-testid="search-other-filters-activities-box" />,
    isCollapsible: true,
    headerExtraContent: <SelectedValuesCounter checkedValuesAtom={activitiesTempManipulatorAtom} />,
    openByDefault: true,
  },
  {
    label: <InstantBookFilterLabel />,
    headerExtraContent: <InstantBookFilterSwitch />,
  },
  {
    label: 'Boat length',
    hideLabel: true,
    content: <AtomSlider atom={boatLengthTempAtom} />,
  },
  {
    label: 'Amenities',
    isCollapsible: false,
    openByDefault: true,
    content: <BoatAmenitiesContent />,
  },
  {
    label: 'Boat category',
    content: <CheckButtonsContent atom={boatCategoriesTempAtom} data-testid="search-other-filters-boat-category-box" />,
    isCollapsible: true,
    headerExtraContent: <SelectedValuesCounter checkedValuesAtom={boatCategoriesManipulatorAtom} />,
    isCategories: true,
  },
];

export default function OtherFiltersContent() {
  const selectedExperienceTags = useAtomValue(selectedExperienceTagAtom);
  const experienceSubTagOptions = useAtomValue(experienceSubTagOptionsAtom);
  const { featureFlag: areExperienceSubTagsEnabled } =
    useFeatureFlagQueryByNameWithAnonymousID('use_experience_sub_tags');

  const otherFilters = useMemo(() => {
    if (areExperienceSubTagsEnabled && experienceSubTagOptions?.length > 0) {
      return [
        {
          label: `${
            (selectedExperienceTags[0]?.charAt(0).toUpperCase() ?? '') + (selectedExperienceTags[0]?.slice(1) ?? '')
          } type`,
          content: <ExperienceSubTagsContent isDropdown />,
          isCollapsible: false,
          isTop: true,
        } as FilterItem,
        ...getOtherFilters(false),
      ];
    }
    return getOtherFilters(true);
  }, []);
  const setTempDurationsAtom = useSetAtom(durationHoursTempAtom);
  const newDurationsVal = useAtomValue(useMemo(() => duplicateCheckboxAtomArrayAtom(durationHoursAtom), []));
  const setTempCaptainOptionsAtom = useSetAtom(captainOptionsTempAtom);
  const newCaptainedVal = useAtomValue(useMemo(() => duplicateCaptainsAtom(), []));
  const setTempPriceAtom = useSetAtom(priceTempAtom);
  const newPriceVal = useAtomValue(useMemo(() => duplicateSliderAtom(priceAtom), []));
  const setActivitiesAtom = useSetAtom(activitiesTempAtom);
  const newActivitiesVal = useAtomValue(useMemo(() => duplicateCheckboxAtomArrayAtom(activitiesAtom), []));
  const setTempAmenitiesAtom = useSetAtom(boatAmenitiesTempAtom);
  const newAmenitiesVal = useAtomValue(useMemo(() => duplicateCheckboxAtomArrayAtom(boatAmenitiesAtom), []));
  const setTempLengthAtom = useSetAtom(boatLengthTempAtom);
  const newLengthVal = useAtomValue(useMemo(() => duplicateSliderAtom(boatLengthAtom), []));
  const setTempCategoriesAtom = useSetAtom(boatCategoriesTempAtom);
  const newCategoriesVal = useAtomValue(useMemo(() => duplicateCheckboxAtomArrayAtom(boatCategoriesAtom), []));
  const [tempInstantBookVal, toggleTempInstantBookAtom] = useAtom(instantBookTempAtom);
  const newInstantBookVal = useAtomValue(instantBookAtom);
  const setTempExperienceAtom = useSetAtom(selectedExperienceTagTempAtom);
  const newExpVal = !selectedExperienceTags?.length ? [] : [...selectedExperienceTags];

  // load current values into temp atoms
  useEffect(() => {
    setTempDurationsAtom(newDurationsVal);
    setTempCaptainOptionsAtom(newCaptainedVal);
    setTempPriceAtom(newPriceVal);
    setActivitiesAtom(newActivitiesVal);
    setTempAmenitiesAtom(newAmenitiesVal);
    setTempLengthAtom(newLengthVal);
    setTempCategoriesAtom(newCategoriesVal);
    setTempExperienceAtom(newExpVal);

    // because this is a toggle, we can toggle the temp atom instead of setting a new value if they are not equal
    if (newInstantBookVal !== tempInstantBookVal) {
      toggleTempInstantBookAtom();
    }
  }, []);

  return (
    <>
      {otherFilters.map(
        ({
          content,
          label,
          hideLabel,
          headerExtraContent,
          isCollapsible,
          openByDefault,
          isPassengers = false,
          isCategories = false,
          isTop,
          key,
        }) => (
          <FilterRow
            openByDefault={openByDefault}
            key={label ? label.toString() : key}
            label={!hideLabel && label}
            headerExtraContent={headerExtraContent}
            isCollapsible={isCollapsible}
            isHeading={false}
            isPassengers={isPassengers}
            isCategories={isCategories}
            isTop={isTop}
          >
            {content}
          </FilterRow>
        )
      )}
    </>
  );
}

const CheckButtonsContent = styled(({ atom, ...others }) => {
  const options = useAtomValue<[]>(atom);
  return <div {...others}>{options.map(renderAtomCheckButton)}</div>;
})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 14px;
`;

const FilterRow = styled(
  ({
    label,
    headerExtraContent,
    children,
    isCollapsible,
    isHeading,
    openByDefault,
    isCategories = false,
    isTop = false,
    ...other
  }) => {
    const breakpoints = useBreakpoint();
    const isMobile = breakpoints.sm;
    const isTablet = breakpoints.lg;
    let padding = '24px 0';
    if (isTop) {
      if (isMobile || isTablet) {
        padding = '40px 0 24px 0';
      } else {
        padding = '12px 0 24px';
      }
    }

    if (isCategories) {
      return (
        <Box padding={padding} {...other}>
          <CollapsibleFilter
            openByDefault={openByDefault}
            headerContent={
              <Box>
                <FlexAlignCenterJustifyStart flex="1 1">
                  <Text fWeight="600" fSize="16px">
                    {label}
                  </Text>
                  {headerExtraContent}
                </FlexAlignCenterJustifyStart>
                <Text fSize="12px" lHeight="1.5">
                  Find the right boat for your trip
                </Text>
              </Box>
            }
          >
            {children}
          </CollapsibleFilter>
        </Box>
      );
    }

    if (isCollapsible) {
      return (
        <Box padding={padding} {...other}>
          <CollapsibleFilter
            openByDefault={openByDefault}
            headerContent={
              <FlexAlignCenterJustifyStart flex="1 1">
                <Text fWeight="600" fSize="16px">
                  {label}
                </Text>
                {headerExtraContent}
              </FlexAlignCenterJustifyStart>
            }
          >
            {children}
          </CollapsibleFilter>
        </Box>
      );
    }
    return (
      <Box padding={padding} {...other}>
        {label && (
          <FlexAlignCenterSpaceBetween mb={children && '32px'}>
            <Text fWeight={isHeading ? '700' : '600'} fSize={isHeading ? '18px' : '16px'}>
              {label}
            </Text>
            {headerExtraContent}
          </FlexAlignCenterSpaceBetween>
        )}
        {children}
      </Box>
    );
  }
)`
  &:not(:last-child) {
    border-bottom: 1px solid #dbdfe5;
  }
  ${({ isHeading }) => isHeading && 'border-bottom: none !important'};
`;

export const OtherFiltersLabel = () => {
  const allFiltersActiveCount = useAtomValue(allFiltersActiveCountAtom);
  const experiencesAtomVal = useAtomValue(selectedExperienceTagAtom);
  const totalExperiences = typeof experiencesAtomVal === 'string' ? 1 : experiencesAtomVal?.length || 0;
  const experiencesCount = totalExperiences > 0 ? totalExperiences - 1 : 0;
  const arrayFiltersCount =
    useAtomValue(boatTypesManipulatorAtom).length +
    useAtomValue(boatCategoriesManipulatorAtom).length +
    useAtomValue(boatMakesManipulatorAtom).length +
    useAtomValue(activitiesManipulatorAtom).length +
    useAtomValue(durationHoursManipulatorAtom).length +
    useAtomValue(boatAmenitiesManipulatorAtom).length;
  const allFiltersCount = arrayFiltersCount + allFiltersActiveCount + experiencesCount;
  return (
    <>
      <FilterIcon /> Filters{allFiltersCount !== 0 && ` (${allFiltersCount})`}
    </>
  );
};

const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path
      d="M13 2.66668H8.33333M5.66667 2.66668H1M13 8.00001H7M4.33333 8.00001H1M13 13.3333H9.66667M7 13.3333H1M8.33333 1.33334V4.00001M4.33333 6.66668V9.33334M9.66667 12V14.6667"
      stroke="#17233C"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
